import { CommentCommunication } from '@seen/core/models/communications/CommentCommunication'
import type { Order } from '@seen/core/models/orders/Order'
import { getReferenceString } from '@seen/core/references'
import { isOfType } from '@seen/core/utils'
import { useQueries } from '@tanstack/react-query'
import { chunk } from 'lodash-es'
import { useMemo } from 'react'
import { useMedplum } from '#src/hooks/useMedplum.ts'

const EMPTY_DATE_MAP = new Map<string, string | undefined>()
const EMPTY_COMMENT_MAP = new Map<string, CommentCommunication>()

interface OrderLastTouchedResult {
  dateMap: Map<string, string | undefined>
  commentByOrder: Map<string, CommentCommunication>
}

export function useOrderLastTouchedMap(
  orders: Order[],
  enabled: boolean
): OrderLastTouchedResult {
  const orderChunks = useMemo(
    () => (enabled ? chunk(orders, 250) : []),
    [orders, enabled]
  )
  const medplum = useMedplum()

  const queries = useQueries({
    queries: orderChunks.map((orderChunk) => ({
      queryKey: ['orders', 'lastTouched', orderChunk.map((order) => order.id)],
      queryFn: () =>
        medplum.searchResourceClassPagesAll(CommentCommunication, {
          ...CommentCommunication.searchQueryAll(),
          'part-of': orderChunk.map((o) => o.reference),
        }),
    })),
  })

  const dataKey = queries.map((q) => q.dataUpdatedAt).join(',')

  const commentList = useMemo(
    () => queries.flatMap((q) => q.data ?? []),
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [dataKey]
  )

  return useMemo<OrderLastTouchedResult>(() => {
    if (!enabled) {
      return { dateMap: EMPTY_DATE_MAP, commentByOrder: EMPTY_COMMENT_MAP }
    }
    const dateMap = new Map<string, string | undefined>()
    const commentByOrder = new Map<string, CommentCommunication>()

    const commentsByOrder = new Map<string, CommentCommunication[]>()
    for (const comment of commentList) {
      for (const partOf of comment.partOf ?? []) {
        if (isOfType(partOf, 'ServiceRequest')) {
          const refString = getReferenceString(partOf)
          commentsByOrder.set(refString, [
            ...(commentsByOrder.get(refString) ?? []),
            comment,
          ])
        }
      }
    }

    for (const [refString, comments] of commentsByOrder.entries()) {
      /* latest comment (any sender) for row display */
      const sorted = comments.sort((a, b) => b.sent.localeCompare(a.sent))
      commentByOrder.set(refString, sorted[0])

      /* latest manual comment for "last touched" filter */
      const manualComments = comments.filter((c) => c.practitionerSender)
      if (manualComments.length > 0) {
        const sortedManual = [...manualComments].sort((a, b) =>
          b.sent.localeCompare(a.sent)
        )
        dateMap.set(refString, sortedManual[0].sent)
      }
    }

    return { dateMap, commentByOrder }
  }, [commentList, enabled])
}
